import React, { useState } from 'react';
import PageLayout from '../components/PageLayout';
import Lightbox from '../components/Lightbox';
import './Events.css';

// Featured video reels of Rayad Kamal Ayub. The card preview uses the
// native <video> element seeked to a frame (#t=) as a lightweight poster,
// then opens full playback in the shared Lightbox on click.
const featuredVideos = [
  {
    src: 'https://pub-ed4dcf9e4976449fa869f76b2ec53b5d.r2.dev/kamal/video-output-C016C242-6675-4A2F-AF89-1C3A9C778C83-1.mov',
    title: 'Tokenized Capital Summit — Highlights',
    caption: 'Rayad Kamal Ayub on stage and on camera at the Tokenized Capital Summit, Miami.'
  },
  {
    src: 'https://pub-ed4dcf9e4976449fa869f76b2ec53b5d.r2.dev/kamal/video-output-57BDE6D0-4E77-4E94-A4B5-6676892C8AB5-1.mov',
    title: 'Family Offices & Allocators — Panel Reel',
    caption: 'Conversation highlights from the Patient Capital in the Fast Markets panel.'
  }
];

// Grouped photo record of Rayad Kamal Ayub's speaking engagements and
// event appearances. Each group renders as its own section; every image
// opens in the shared Lightbox on click.
const eventGroups = [
  {
    id: 'gamma-prime-miami',
    title: 'Family Offices & Allocators: Patient Capital in the Fast Markets',
    meta: 'Gamma Prime — Tokenized Capital Summit, Miami · 2026',
    description:
      "Rayad Kamal Ayub (Rayad Group Dubai) joined Chaman Malhi (Gamma Prime), Pete Najarian (Raptor Digital), Bundeep Singh Rangar (STBL) and Nick Ducoff (Solana Foundation) on the family-office panel, articulating the patient-capital playbook across ETFs, Bitcoin, AI and cybersecurity.",
    images: [
      { src: '/images/events/family-offices-panel-stage.jpeg', alt: 'Family Offices & Allocators panel on stage at Gamma Prime, Miami' },
      { src: '/images/events/family-offices-panel-speaking.jpeg', alt: 'Rayad Kamal Ayub speaking on the Family Offices & Allocators panel' },
      { src: '/images/events/family-offices-panel-wide.jpeg', alt: 'Family Offices & Allocators panel before a full audience' },
      { src: '/images/events/family-offices-panel-audience.png', alt: 'Family Offices & Allocators panel viewed from the audience' },
      { src: '/images/events/family-offices-panel-room.jpeg', alt: 'Family Offices & Allocators panel and conference room' }
    ]
  },
  {
    id: 'tokenized-capital-summit',
    title: 'Tokenized Capital Summit 2026 — Miami',
    meta: 'Gamma Prime, in partnership with Peanut · 2026',
    description:
      'On-site media interview at the Tokenized Capital Summit, discussing Rayad Group’s measured approach to digital assets, tokenization and long-horizon allocation.',
    images: [
      { src: '/images/events/tokenized-capital-summit-miami-interview.jpeg', alt: 'Rayad Kamal Ayub interviewed at Tokenized Capital Summit 2026, Miami' }
    ]
  },
  {
    id: 'foundation-ventures-hong-kong',
    title: 'VIP Panel — Hong Kong',
    meta: 'Foundation Ventures × Gamma Prime · 9 February',
    description:
      'Rayad Kamal Ayub on the Foundation Ventures VIP investor panel in Hong Kong, exploring cross-border capital flows and emerging-market opportunities.',
    images: [
      { src: '/images/events/foundation-ventures-vip-panel-hong-kong.jpeg', alt: 'Foundation Ventures x Gamma Prime VIP Panel, Hong Kong' },
      { src: '/images/events/gamma-prime-vip-panel.jpeg', alt: 'Gamma Prime VIP Panel with Rayad Ayub and fellow allocators' }
    ]
  },
  {
    id: 'mrk-art-gallery',
    title: 'MRK Contemporary Art Gallery — Speaking Engagements',
    meta: 'Pullman Dubai & Annual Student Art Show',
    description:
      'Rayad Kamal Ayub addressing guests at an MRK Contemporary Art Gallery exhibition at Pullman Dubai, and on the red carpet at the GCC’s largest Annual Student Art Show.',
    images: [
      { src: '/images/events/mrk-art-gallery-pullman-talk.jpeg', alt: 'Rayad Kamal Ayub speaking at an art exhibition, Pullman Dubai' },
      { src: '/images/events/mrk-art-gallery-pullman-speech.jpeg', alt: 'Rayad Kamal Ayub addressing guests at MRK Contemporary Art Gallery' },
      { src: '/images/events/annual-student-art-show.jpeg', alt: 'Rayad Kamal Ayub at the Annual Student Art Show, the GCC’s largest' }
    ]
  }
];

const PlayIcon = () => (
  <span className="events-play" aria-hidden="true">
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="32" fill="rgba(255,255,255,0.92)" />
      <path d="M26 21v22l18-11-18-11z" fill="#806a4d" />
    </svg>
  </span>
);

const Events = () => {
  const [lightbox, setLightbox] = useState({ open: false, src: '', alt: '', type: 'image' });

  const openMedia = (src, alt, type) => {
    setLightbox({ open: true, src, alt, type: type || 'image' });
  };

  const closeMedia = () => {
    setLightbox({ open: false, src: '', alt: '', type: 'image' });
  };

  return (
    <PageLayout
      title="Events & Speaking"
      subtitle="Rayad Kamal Ayub on global stages — family-office and tokenized-capital panels, investor summits, and contemporary art engagements."
    >
      <div className="events-container">
        <section className="events-group events-featured">
          <header className="events-group-header">
            <span className="events-group-meta">Featured Reels</span>
            <h2 className="events-group-title">On Camera</h2>
            <p className="events-group-desc">
              Watch the highlights — tap any reel to play. All photos and videos
              on this page open full-screen.
            </p>
          </header>
          <div className="events-video-grid">
            {featuredVideos.map((video) => (
              <figure
                key={video.src}
                className="events-video-card"
                onClick={() => openMedia(video.src, video.title, 'video')}
              >
                <div className="events-video-frame">
                  <video
                    src={`${video.src}#t=0.5`}
                    preload="metadata"
                    muted
                    playsInline
                    tabIndex={-1}
                  />
                  <PlayIcon />
                </div>
                <figcaption>
                  <span className="events-video-title">{video.title}</span>
                  <span className="events-video-caption">{video.caption}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {eventGroups.map((group) => (
          <section key={group.id} className="events-group">
            <header className="events-group-header">
              <span className="events-group-meta">{group.meta}</span>
              <h2 className="events-group-title">{group.title}</h2>
              <p className="events-group-desc">{group.description}</p>
            </header>
            <div className="events-grid">
              {group.images.map((img) => (
                <figure
                  key={img.src}
                  className="events-photo"
                  onClick={() => openMedia(img.src, img.alt, 'image')}
                >
                  <div className="events-photo-frame">
                    <img src={img.src} alt={img.alt} loading="lazy" />
                    <span className="events-zoom" aria-hidden="true">⤢</span>
                  </div>
                  <figcaption>{img.alt}</figcaption>
                </figure>
              ))}
            </div>
          </section>
        ))}
      </div>
      <Lightbox
        isOpen={lightbox.open}
        imageSrc={lightbox.src}
        imageAlt={lightbox.alt}
        mediaType={lightbox.type}
        onClose={closeMedia}
      />
    </PageLayout>
  );
};

export default Events;
